import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpErrorResponse, HttpResponse } from '@angular/common/http';

import { catchError, EMPTY, from, lastValueFrom, Observable, of, tap, throwError } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { SnackBarService } from '../services/snackbar.service';
import { SnackBarType } from '../models/Snackbar';
import { environment } from 'src/environments/environment';
import { SpinnerService } from 'is-lib';
import { RequestCacheService } from '../services/requestCache.service';
import { RequestCacheConfig } from '../models/Request';


@Injectable()
export class JwtInterceptor implements HttpInterceptor {
    constructor(private _authService: AuthService, private _snackbarService: SnackBarService, private _spinnerService: SpinnerService, private _requestCacheService: RequestCacheService) { }

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        let duplicateRequest = this._requestCacheService.checkForDuplicateRequest(request.method, request.urlWithParams, request.body)
       
        if (duplicateRequest) {
            return duplicateRequest.observable;
        }

        let expirationTimer = request.headers.has('ExpirationTimer') ? +request.headers.get('ExpirationTimer')! : 0;
        

        // add authorization header with jwt token if available
        if (!request.headers.has('InterceptorSkipHeader')) {
            let authRequest$ = this.handleRequestWithAuth(request, next);
            
            return this._requestCacheService.saveRequestToCache(request, authRequest$, new RequestCacheConfig(expirationTimer));
        } else {
            return this._requestCacheService.saveRequestToCache(request, next.handle(request));
        }
    }

    validateToken(): string | undefined | null {
        let token = null
        try {
            token = this._authService.authorizationHeaderValue;
            
            const tokenExpired = this._authService.tokenExpired;
            if (tokenExpired) {
                return null;
            }
        } catch (error) {
            return undefined;
        }

        return token;
    }

    handleRequestWithAuth(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        if (this._authService.tokenExpired) {
            // signout() ignores repeated calls, so every request that finds the token expired can ask for it
            this._authService.signout();

            // Fails the request instead of faking a response, so callers hit their error handlers instead of reading data from an empty body
            return throwError(() => new HttpErrorResponse({
                status: 401,
                statusText: 'Unauthorized',
                url: request.urlWithParams,
                error: { statusCode: 401, message: 'Session expired' }
            }));
        }

        if (request.url.includes(environment.apiUrl)) {
            this._authService.renewUserSignIn();

            request = this.addTokenToRequest(request);
        }  

        return next.handle(request);
    }

    addTokenToRequest(request: HttpRequest<any>) {
        if (this._authService.userData != null) {
            let token = this.validateToken();

            if (token) {
                request = request.clone({
                    setHeaders: {
                        Authorization: token
                    }
                });
            } else if (token === null){
                this._authService.signout();
            }
        }
        return request;
    }
}